/**
 * thresholds.ts — versioned validation thresholds for the diagnostics rule engine.
 *
 * Values are the MVP defaults derived in native-diagnostics-validation §5 / §7.
 * They are intentionally pure data so they can be tuned against real-device
 * measurements without touching the rule logic. Bump `THRESHOLD_VERSION` whenever
 * a value changes so reports/exports stay traceable (spec §7, §8).
 *
 * Convention: `warn`/`fail` numbers are ascending bands (higher = worse) unless a
 * comment says otherwise. Duration ratios are fractions of the relevant unit
 * (buffer duration, hop duration, event interval).
 */

export const THRESHOLD_VERSION = 'mvp-1';

export const DIAGNOSTICS_THRESHOLDS = {
  /** audio.callback.budget — callback avg as fraction of buffer duration (§5.2). */
  callbackBudget: { warn: 0.25, fail: 0.5 },
  /** audio.callback.alive — last callback age as multiples of buffer duration (§5.2). */
  callbackAge: { warn: 2, fail: 5 },
  /** audio.sample_rate.supported — analysis-grade capture rates (§5.2). */
  supportedSampleRates: [44100, 48000] as const,
  /** ring.backlog — backlog in ms (§5.3 / §7). */
  ringBacklogMs: { warn: 100, fail: 500 },
  /** ring.fill_ratio — occupancy fraction (§5.3). */
  ringFillRatio: { warn: 0.5, fail: 0.8 },
  /** analysis.avg_time — avg process time as fraction of hop duration (§5.4). */
  analysisAvg: { warn: 0.4, fail: 0.6 },
  /** analysis.p95_time — p95 process time as fraction of hop duration (§5.4 / §7). */
  analysisP95: { warn: 0.6, fail: 0.9 },
  /** analysis.last_frame_age — last frame age as multiples of hop duration (§5.4). */
  analysisFrameAge: { warn: 2, fail: 5 },
  /** detector.confidence — avg confidence below this is abnormally low (§5.5). */
  detectorConfidenceWarn: 0.3,
  /** post.hold_rate — held frames as fraction of analysis frames (§5.6). */
  postHoldRate: { warn: 0.2, fail: 0.5 },
  /** post.interpolation_rate — interpolated frames as fraction of frames (§5.6). */
  postInterpolationRate: { warn: 0.2, fail: 0.5 },
  /** event.queue_depth — pending dispatch queue depth (§5.7). */
  eventQueueDepth: { warn: 4, fail: 16 },
  /** event.drop_rate — dropped / (emitted + dropped) pitch frames (§5.7). */
  eventDropRate: { warn: 0.02, fail: 0.1 },
  /** event.last_age — last event age as multiples of pitch interval (§5.7). */
  eventLastAge: { warn: 2, fail: 5 },
  /** ui.consumer_lag — JS consumer lag in ms (§5.8). */
  uiConsumerLagMs: { warn: 50, fail: 150 },
  /** degradation.level — sustained level bands (§5.9). */
  degradationLevel: { warn: 1, fail: 3 },
} as const;
